import { useEffect, useMemo, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import {
  Users,
  UserCheck,
  UserPlus,
  Clock,
  CheckCircle2,
  XCircle,
  Mail,
  Phone,
  RefreshCw,
  Loader2
} from 'lucide-react';

export default function ResponsaveisPage() {
  const [solicitacoes, setSolicitacoes] = useState([]);
  const [responsaveis, setResponsaveis] = useState([]);
  const [alunos, setAlunos] = useState([]);

  const [loading, setLoading] = useState(true);
  const [acao, setAcao] = useState('');
  const [filhoSelecionado, setFilhoSelecionado] = useState({});
  const [parentescoSelecionado, setParentescoSelecionado] = useState({});

  const carregar = async () => {
    setLoading(true);

    try {
      const [
        solicitacoesRes,
        responsaveisRes,
        alunosRes
      ] = await Promise.all([
        api.get('/responsavel/solicitacoes'),
        api.get('/responsavel/gestor/responsaveis'),
        api.get('/gestor/alunos')
      ]);

      setSolicitacoes(solicitacoesRes.data || []);
      setResponsaveis(responsaveisRes.data || []);
      setAlunos(alunosRes.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const aprovar = async (solicitacao) => {
    const alunoId = solicitacao.aluno_id;

    setAcao(`aprovar-${alunoId}`);

    try {
      await api.post(
        `/responsavel/solicitacoes/${alunoId}/aprovar`
      );

      toast.success(
        `Responsável de ${solicitacao.aluno_nome || 'aluno'} aprovado.`
      );

      await carregar();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAcao('');
    }
  };

  const recusar = async (solicitacao) => {
    const alunoId = solicitacao.aluno_id;

    const motivo = window.prompt(
      `Informe o motivo da recusa da solicitação de ${
        solicitacao.aluno_nome || 'este aluno'
      }:`
    );

    if (motivo === null) return;

    if (!motivo.trim()) {
      toast.error('Informe o motivo da recusa.');
      return;
    }

    const confirmar = window.confirm(
      `Confirmar a recusa da solicitação de ${
        solicitacao.aluno_nome || 'este aluno'
      }?`
    );

    if (!confirmar) return;

    setAcao(`recusar-${alunoId}`);

    try {
      await api.post(
        `/responsavel/solicitacoes/${alunoId}/recusar`,
        {
          motivo: motivo.trim()
        }
      );

      toast.success('Solicitação recusada.');

      await carregar();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAcao('');
    }
  };

  const idsJaVinculados = useMemo(() => {
    const ids = new Set();

    responsaveis.forEach(responsavel => {
      (responsavel.filhos || []).forEach(filho => {
        if (filho.id) {
          ids.add(filho.id);
        }
      });
    });

    return ids;
  }, [responsaveis]);

  const filhosDisponiveis = (responsavel) => {
    const idsDesteResponsavel = new Set(
      (responsavel.filhos || []).map(filho => filho.id)
    );

    return alunos.filter(aluno => {
      if (!aluno?.id) return false;

      if (idsDesteResponsavel.has(aluno.id)) {
        return false;
      }

      /*
       * Nesta V1 cada aluno fica vinculado a um responsável principal.
       * Evita selecionar aluno já ligado a outro responsável.
       */
      if (idsJaVinculados.has(aluno.id)) {
        return false;
      }

      return aluno.status !== 'pre_cadastro';
    });
  };

  const vincularFilho = async (responsavel) => {
    const alunoId = filhoSelecionado[responsavel.id];
    const parentesco = parentescoSelecionado[responsavel.id];

    if (!alunoId) {
      toast.error('Selecione um aluno.');
      return;
    }

    if (!parentesco) {
      toast.error('Selecione o parentesco.');
      return;
    }

    setAcao(`vincular-${responsavel.id}`);

    try {
      await api.post(
        `/responsavel/vincular/${responsavel.id}/${alunoId}`,
        {
          parentesco
        }
      );

      toast.success('Filho vinculado com sucesso.');

      setFilhoSelecionado(prev => ({
        ...prev,
        [responsavel.id]: ''
      }));

      setParentescoSelecionado(prev => ({
        ...prev,
        [responsavel.id]: ''
      }));

      await carregar();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAcao('');
    }
  };

  const desvincularFilho = async (
    responsavel,
    filho
  ) => {
    const confirmar = window.confirm(
      `Desvincular ${filho.name || 'este aluno'} de ${
        responsavel.name || 'este responsável'
      }?`
    );

    if (!confirmar) return;

    const chave = `desvincular-${responsavel.id}-${filho.id}`;

    setAcao(chave);

    try {
      await api.post(
        `/responsavel/desvincular/${responsavel.id}/${filho.id}`
      );

      toast.success(
        'Responsável desvinculado do aluno.'
      );

      await carregar();

    } catch (err) {
      toast.error(
        formatApiError(err)
      );

    } finally {
      setAcao('');
    }
  };


  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        Carregando responsáveis...
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">
            Família
          </p>

          <h1 className="text-3xl font-black font-['Barlow_Condensed'] uppercase text-white mt-1">
            Responsáveis
          </h1>

          <p className="text-sm text-zinc-500 mt-1">
            Aprove solicitações e gerencie os filhos vinculados.
          </p>
        </div>

        <Button
          variant="outline"
          onClick={carregar}
          className="border-[#27272A] text-zinc-300 hover:bg-[#1C1C1E]"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Atualizar
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>

              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">
                  Pendentes
                </p>

                <p className="text-2xl font-bold text-white">
                  {solicitacoes.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <UserCheck className="w-5 h-5 text-green-400" />
              </div>

              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">
                  Responsáveis
                </p>

                <p className="text-2xl font-bold text-white">
                  {responsaveis.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-400" />
              </div>

              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">
                  Filhos vinculados
                </p>

                <p className="text-2xl font-bold text-white">
                  {
                    responsaveis.reduce(
                      (total, responsavel) =>
                        total + (responsavel.filhos || []).length,
                      0
                    )
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-bold text-white">
            Solicitações pendentes
          </h2>

          <p className="text-sm text-zinc-500">
            Pedidos enviados pelos próprios alunos.
          </p>
        </div>

        {solicitacoes.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent className="p-8 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-3" />

              <p className="text-white font-medium">
                Nenhuma solicitação pendente
              </p>

              <p className="text-sm text-zinc-500 mt-1">
                Quando um aluno cadastrar um responsável,
                a solicitação aparecerá aqui.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {solicitacoes.map(solicitacao => (
              <Card
                key={solicitacao.aluno_id}
                className="bg-[#141414] border-[#27272A]"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-zinc-500">
                        Aluno
                      </p>

                      <h3 className="text-lg font-bold text-white mt-1">
                        {solicitacao.aluno_nome || 'Aluno'}
                      </h3>
                    </div>

                    <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                      <Clock className="w-3.5 h-3.5 mr-1" />
                      Pendente
                    </Badge>
                  </div>

                  <div className="mt-5 p-4 rounded-xl bg-[#0A0A0A] border border-[#27272A]">
                    <p className="text-xs uppercase tracking-wider text-zinc-500">
                      Responsável solicitado
                    </p>

                    <p className="text-white font-semibold mt-2">
                      {solicitacao.responsavel_nome}
                    </p>

                    <p className="text-sm text-zinc-400 mt-1">
                      {solicitacao.parentesco || 'Parentesco não informado'}
                    </p>

                    <div className="space-y-2 mt-4">
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Mail className="w-4 h-4" />
                        <span className="break-all">
                          {solicitacao.responsavel_email}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Phone className="w-4 h-4" />
                        <span>
                          {solicitacao.responsavel_whatsapp || 'Não informado'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 mt-5">
                    <Button
                      onClick={() => aprovar(solicitacao)}
                      disabled={!!acao}
                      className="bg-green-600 hover:bg-green-700 flex-1"
                    >
                      {acao === `aprovar-${solicitacao.aluno_id}` ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                      )}

                      Aprovar
                    </Button>

                    <Button
                      variant="outline"
                      onClick={() => recusar(solicitacao)}
                      disabled={!!acao}
                      className="border-red-500/20 text-red-400 hover:bg-red-500/10 flex-1"
                    >
                      {acao === `recusar-${solicitacao.aluno_id}` ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <XCircle className="w-4 h-4 mr-2" />
                      )}

                      Recusar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-4">
        <div>
          <h2 className="text-xl font-bold text-white">
            Responsáveis aprovados
          </h2>

          <p className="text-sm text-zinc-500">
            Cada responsável pode ter mais de um filho vinculado.
          </p>
        </div>

        {responsaveis.length === 0 ? (
          <Card className="bg-[#141414] border-[#27272A]">
            <CardContent className="p-8 text-center">
              <Users className="w-10 h-10 text-zinc-600 mx-auto mb-3" />

              <p className="text-white font-medium">
                Nenhum responsável cadastrado
              </p>

              <p className="text-sm text-zinc-500 mt-1">
                O primeiro aparecerá após uma solicitação ser aprovada.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {responsaveis.map(responsavel => {
              const disponiveis = filhosDisponiveis(responsavel);

              return (
                <Card
                  key={responsavel.id}
                  className="bg-[#141414] border-[#27272A]"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-green-500/10 flex items-center justify-center shrink-0">
                        <UserCheck className="w-6 h-6 text-green-400" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-bold text-white">
                            {responsavel.name}
                          </h3>

                          <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                            Ativo
                          </Badge>
                        </div>

                        <div className="space-y-1 mt-2 text-sm text-zinc-400">
                          <div className="flex items-center gap-2">
                            <Mail className="w-4 h-4 shrink-0" />
                            <span className="break-all">
                              {responsavel.email}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4 shrink-0" />
                            <span>
                              {responsavel.phone || 'Não informado'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">
                        Filhos vinculados
                      </p>

                      <div className="space-y-2 mt-3">
                        {(responsavel.filhos || []).length === 0 ? (
                          <p className="text-sm text-zinc-500">
                            Nenhum filho vinculado.
                          </p>
                        ) : (
                          responsavel.filhos.map(filho => (
                            <div
                              key={filho.id}
                              className="flex items-center justify-between gap-3 p-3 rounded-xl bg-[#0A0A0A] border border-[#27272A]"
                            >
                              <div>
                                <p className="text-white font-medium">
                                  {filho.name}
                                </p>

                                <p className="text-xs text-zinc-500">
                                  {filho.parentesco || 'Vínculo familiar'}
                                </p>
                              </div>

                              <div className="flex items-center gap-2 shrink-0">
                                <CheckCircle2 className="w-4 h-4 text-green-400" />

                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    desvincularFilho(
                                      responsavel,
                                      filho
                                    )
                                  }
                                  disabled={!!acao}
                                  className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                                >
                                  {acao ===
                                  `desvincular-${responsavel.id}-${filho.id}` ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <XCircle className="w-4 h-4" />
                                  )}

                                  <span className="ml-2">
                                    Desvincular
                                  </span>
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    <div className="mt-6 pt-5 border-t border-[#27272A]">
                      <div className="flex items-center gap-2 mb-3">
                        <UserPlus className="w-4 h-4 text-zinc-400" />

                        <p className="text-sm font-medium text-zinc-300">
                          Vincular outro filho
                        </p>
                      </div>

                      {disponiveis.length === 0 ? (
                        <p className="text-sm text-zinc-500">
                          Não há outros alunos disponíveis para vínculo.
                        </p>
                      ) : (
                        <div className="flex flex-col gap-2">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <select
                              value={filhoSelecionado[responsavel.id] || ''}
                              onChange={e =>
                                setFilhoSelecionado(prev => ({
                                  ...prev,
                                  [responsavel.id]: e.target.value
                                }))
                              }
                              className="h-10 rounded-md border border-[#27272A] bg-[#0A0A0A] px-3 text-sm text-white outline-none"
                            >
                              <option value="">
                                Selecione o aluno
                              </option>

                              {disponiveis.map(aluno => (
                                <option
                                  key={aluno.id}
                                  value={aluno.id}
                                >
                                  {aluno.name}
                                </option>
                              ))}
                            </select>

                            <select
                              value={parentescoSelecionado[responsavel.id] || ''}
                              onChange={e =>
                                setParentescoSelecionado(prev => ({
                                  ...prev,
                                  [responsavel.id]: e.target.value
                                }))
                              }
                              className="h-10 rounded-md border border-[#27272A] bg-[#0A0A0A] px-3 text-sm text-white outline-none"
                            >
                              <option value="">
                                Selecione o parentesco
                              </option>
                              <option value="Mãe">Mãe</option>
                              <option value="Pai">Pai</option>
                              <option value="Avó">Avó</option>
                              <option value="Avô">Avô</option>
                              <option value="Tia">Tia</option>
                              <option value="Tio">Tio</option>
                              <option value="Responsável legal">
                                Responsável legal
                              </option>
                              <option value="Outro">Outro</option>
                            </select>
                          </div>

                          <Button
                            onClick={() => vincularFilho(responsavel)}
                            disabled={
                              !filhoSelecionado[responsavel.id] ||
                              !parentescoSelecionado[responsavel.id] ||
                              !!acao
                            }
                            className="bg-[var(--cor-primaria,#007AFF)] hover:opacity-90"
                          >
                            {acao === `vincular-${responsavel.id}` ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <UserPlus className="w-4 h-4 mr-2" />
                            )}

                            Vincular
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
